import { button } from "../../design-system/components/button.ts";
import { card } from "../../design-system/components/card.ts";
import { notice } from "../../design-system/components/feedback.ts";
import { icon } from "../../design-system/components/icon.ts";
import { segmented } from "../../design-system/components/segmented.ts";
import { el } from "../../design-system/dom.ts";
import { ClaimFlow } from "../receiver/claim-experience.ts";
import { claimSurface } from "../receiver/claim-surface.ts";
import {
  DEFAULT_SCENARIO,
  type ReceiverScenario,
  type RecipientKind,
  SyntheticClaimProvider,
  type SyntheticOutcome,
} from "../receiver/synthetic-claim-provider.ts";
import type { RouteView } from "../router.ts";
import type { ShellState } from "../state.ts";
import { TENANTS } from "../tenants.ts";

/**
 * The internal reference entry to the receiver claim experience.
 *
 * ## Why the claimant surface has no route
 *
 * The obvious way to open a receiver experience is a URL, and it is the one way
 * this milestone may not use. A `/claim/:id` route — even a synthetic one — would
 * be a claim entry point in the shape of the real thing, and
 * `docs/architecture/DECISION_HISTORY.md` § 7 records that reintroducing a
 * Nura-hosted claim shell "is a claim-security architecture change requiring an
 * ADR, not a UX enhancement". Phase 02 **PV-16 is NOT TESTED**, and where the
 * production claimant surface is hosted is undecided.
 *
 * So the surface is **not routable at all**. It has no path, and this page opens
 * it as an overlay from an explicit control. Nothing about that mechanism is a
 * proposal for production: it is the least production-shaped way to put the
 * experience on screen, chosen precisely so that no one can mistake it for the
 * real entry point. The URL is unchanged by opening it, and
 * `frontend/test/receiver-boundary.test.ts` asserts that.
 *
 * ## Where the engineering controls live
 *
 * Here, and never inside the surface. The scenario dials below choose which
 * synthetic fixture the surface is built from; once it is open, everything on
 * screen is product. A person reviewing the experience is looking at the product
 * and a person reviewing the fixtures is looking at this page, and the two do not
 * overlap.
 *
 * Selections are memory-only. Nothing here is written to storage — a persisted
 * "returning recipient" flag would be a synthetic enrollment pretending to be a
 * real one, which is exactly the impression this milestone must not create.
 */

/** The scenario the next open will use. Memory-only, deliberately. */
let scenario: ReceiverScenario = DEFAULT_SCENARIO;

/** Reset the scenario. Test support only. */
export function resetReceiverScenario(): void {
  scenario = DEFAULT_SCENARIO;
}

/** The scenario currently selected, for tests and for the summary line. */
export function receiverScenario(): ReceiverScenario {
  return scenario;
}

const RECIPIENT_OPTIONS: ReadonlyArray<{ value: RecipientKind; label: string }> = [
  { value: "first-time", label: "First-time recipient" },
  { value: "returning", label: "Returning recipient" },
];

const OUTCOME_OPTIONS: ReadonlyArray<{ value: SyntheticOutcome; label: string }> = [
  { value: "completes", label: "Claim completes" },
  { value: "unavailable", label: "Claim cannot complete" },
];

/** One line of the "what this is not" list. */
function boundaryItem(text: string): HTMLElement {
  return el("li", { class: "nura-claim-boundaries__item" }, [
    el("span", { class: "nura-claim-boundaries__marker", staticHtml: icon("prohibit", 14) }),
    el("span", { text }),
  ]);
}

interface OpenOverlay {
  readonly dialog: HTMLDialogElement;
  close(): void;
}

/**
 * Open the synthetic claimant experience over the reference page.
 *
 * A native `<dialog>` opened with `showModal`, so the focus trap, the Escape
 * key, background inertness and the dialog role come from the platform rather
 * than from a hand-rolled overlay that would get at least one of them wrong.
 */
function openClaimExperience(
  state: ShellState,
  returnFocusTo: HTMLElement,
  onClosed: () => void,
): OpenOverlay {
  const dialog = el("dialog", {
    class: "nura-receiver-dialog",
    attrs: { "aria-label": "Receiver claim experience — internal reference" },
  });

  const flow = new ClaimFlow(new SyntheticClaimProvider({ scenario, tenant: state.tenant }));
  const surface = claimSurface({
    state,
    flow,
    onFinish: () => {
      dialog.close();
    },
  });

  const close = button({
    label: "Close",
    variant: "ghost",
    size: "sm",
    icon: "x-circle",
    onClick: () => {
      dialog.close();
    },
  });
  close.setAttribute("data-reference-control", "true");

  /*
   * The internal bar sits outside the surface, above it, and looks like what it
   * is. Everything below the rule is the product; everything on this line is
   * scaffolding, including this button.
   */
  const bar = el(
    "div",
    { class: "nura-receiver-bar", attrs: { "data-reference-control": "true" } },
    [
      el("div", { class: "nura-receiver-bar__text" }, [
        el("p", { class: "nura-receiver-bar__title", text: "Internal reference" }),
        el("p", {
          class: "nura-receiver-bar__detail",
          text: `${scenario.recipient === "first-time" ? "First-time recipient" : "Returning recipient"} · ${
            scenario.outcome === "completes" ? "claim completes" : "claim cannot complete"
          } · ${state.tenant.name}`,
        }),
      ]),
      close,
    ],
  );

  dialog.append(
    el("div", { class: "nura-receiver-dialog__inner" }, [
      bar,
      el("div", { class: "nura-receiver-frame" }, [surface.root]),
    ]),
  );

  dialog.addEventListener("close", () => {
    surface.destroy();
    dialog.remove();
    onClosed();
    returnFocusTo.focus({ preventScroll: true });
  });

  document.body.appendChild(dialog);
  dialog.showModal();
  surface.focus();

  return {
    dialog,
    close(): void {
      dialog.close();
    },
  };
}

export function receiverReferenceRoute(state: ShellState): RouteView {
  let overlay: OpenOverlay | undefined;

  const open = button({
    label: "Open receiver claim experience",
    variant: "primary",
    block: true,
    icon: "arrow-right",
    iconPosition: "end",
    onClick: () => {
      overlay = openClaimExperience(state, open, () => {
        overlay = undefined;
      });
    },
  });
  open.setAttribute("data-reference-control", "true");

  /**
   * What the next open will show, in one line.
   *
   * A stakeholder demo is a sequence of "now show me the returning one" — so
   * the page states what is currently selected rather than making someone read
   * three controls back to confirm it.
   */
  const summary = el("p", { class: "nura-claim-scenario__summary" });

  function syncSummary(): void {
    const tenantName = state.tenant.name;
    const who =
      scenario.recipient === "first-time"
        ? "a first-time recipient, who supplies what the bank requires"
        : "a returning recipient, who claims in one press";
    const ending = scenario.outcome === "completes" ? "the claim completes" : "the claim fails";
    summary.textContent = `Opens ${tenantName} for ${who} — ${ending}.`;
  }

  const tenant = segmented<string>({
    label: "Bank",
    options: TENANTS.map((candidate) => ({ value: candidate.id, label: candidate.name })),
    value: state.tenant.id,
    onChange: (value) => {
      const match = TENANTS.find((candidate) => candidate.id === value);
      if (match !== undefined) state.setTenant(match);
      syncSummary();
    },
  });

  const recipient = segmented<RecipientKind>({
    label: "Recipient",
    options: RECIPIENT_OPTIONS,
    value: scenario.recipient,
    onChange: (value) => {
      scenario = { ...scenario, recipient: value };
      syncSummary();
    },
  });

  const outcome = segmented<SyntheticOutcome>({
    label: "Synthetic outcome",
    options: OUTCOME_OPTIONS,
    value: scenario.outcome,
    onChange: (value) => {
      scenario = { ...scenario, outcome: value };
      syncSummary();
    },
  });

  syncSummary();

  const node = el("div", { class: "nura-stack" }, [
    el("div", { class: "nura-page-header" }, [
      el("p", { class: "nura-eyebrow", text: "Internal reference" }),
      el("h1", { class: "nura-page-header__title", text: "Receiver claim experience" }),
      el("p", {
        class: "nura-lede",
        text: "The other half of the sender flow: what a person sees when money is waiting for them, including the details the sending bank needs before it can deliver it. Synthetic throughout — the experience is the deliverable, and none of the mechanics behind it exist.",
      }),
    ]),

    notice({
      tone: "attention",
      title: "Not a production claim entry point",
      text: "This control is internal scaffolding. Where the real claimant experience is hosted is undecided and is not decided here, the surface has no route or URL of its own, and no claim link, claim secret or claim capability exists anywhere in this build. Recipient details typed into the demo are never transmitted, stored or logged.",
    }),

    card(
      {
        title: "Scenario",
        note: "Deterministic fixtures. The same selection produces the same screens every time, and each tenant asks for the details its own configuration declares.",
        headingLevel: "h2",
      },
      [
        el("div", { class: "nura-claim-scenario" }, [
          el("div", { class: "nura-claim-scenario__row" }, [
            el("p", { class: "nura-claim-scenario__label", text: "Bank" }),
            tenant.root,
          ]),
          el("div", { class: "nura-claim-scenario__row" }, [
            el("p", { class: "nura-claim-scenario__label", text: "Recipient" }),
            recipient.root,
          ]),
          el("div", { class: "nura-claim-scenario__row" }, [
            el("p", { class: "nura-claim-scenario__label", text: "Synthetic outcome" }),
            outcome.root,
          ]),
        ]),
        summary,
        el("div", { class: "nura-actions" }, [open]),
      ],
    ),

    card(
      {
        title: "What this does not implement",
        note: "Each item is owned by validation or by an architecture decision that is still open.",
        headingLevel: "h2",
      },
      [
        el("ul", { class: "nura-claim-boundaries" }, [
          boundaryItem("No claim link, claim secret or claim capability — PV-16 is not tested."),
          boundaryItem("No eligibility, linkage to a transfer, expiry or single-use consumption."),
          boundaryItem("No payout, settlement, bank API or receiver account lookup."),
          boundaryItem(
            "No transmission of recipient details: what is typed stays in the browser, is never sent, stored or logged, and is discarded when the screen is left.",
          ),
          boundaryItem(
            "No recipient enrollment: in production the sending bank collects, validates and holds these details and returns only a reference. Where that collection happens is an open architecture question.",
          ),
          boundaryItem(
            "No bank credential is ever requested — no sign-in, password, PIN, one-time code, card PIN or biometric — and none can be configured.",
          ),
          boundaryItem(
            "No confirmation number, receipt, reference or timestamp on success: a real completion is reported by the bank, and that mechanism is not implemented.",
          ),
          boundaryItem(
            "No failure semantics: why a claim does not complete, what to do next, and whether a second attempt is permitted are the bank's, and no reason is invented.",
          ),
          boundaryItem("No network request, no storage and no analytics of any kind."),
          boundaryItem("No hosting or deployment model for the production claimant surface."),
        ]),
        el("p", {
          class: "nura-hint",
          text: "Open question: a bank may require the recipient to confirm entered receiving details — an IBAN in particular — before submission. That is a bank requirement rather than a Nura preference, so it would arrive as configuration alongside the field set. It is deliberately not modelled here, and the default flow stays at two actions.",
        }),
        el("p", {
          // The file path is one long unbreakable token, which overflowed a
          // 320pt viewport until it was allowed to break mid-word.
          class: "nura-hint nura-claim-source",
          text: "Recorded in docs/roadmap/PHASE_03_CLAIM_EXPERIENCE_SCOPE.md, and enforced by the receiver-claim-boundary gate rather than by this paragraph.",
        }),
      ],
    ),
  ]);

  return {
    node,
    destroy(): void {
      overlay?.close();
    },
  };
}
